import React, { useState, useEffect } from 'react';
import apiClient from '../../assets/scripts/services/apiClient';

const ApplicationPagesModal = ({ isOpen, onClose, application }) => {
  const [pages, setPages] = useState([]);
  const [newPage, setNewPage] = useState({ page_title: '', url: '', restricted_access_options: {} });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingPage, setEditingPage] = useState(null);

  useEffect(() => {
    if (isOpen && application) {
      loadApplicationPages();
    }
  }, [isOpen, application]);

  const loadApplicationPages = async () => {
    setLoading(true);
    try {
      // Load pages using the page_registry API
      const response = await apiClient.get(`/applications/${application.id}/pages`);
      console.log('Loaded pages from API:', response);
      
      // The API returns an array of pages or a paginated response
      if (Array.isArray(response)) {
        setPages(response);
      } else if (response && response.items) {
        setPages(response.items);
      } else {
        setPages([]);
      }
    } catch (err) {
      console.error('Error loading application pages:', err);
      // If endpoint doesn't exist or returns 404, try empty array
      setPages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPage = async () => {
    // Validate new page
    const newErrors = {};
    if (!newPage.page_title.trim()) {
      newErrors.page_title = 'Page title is required';
    }
    if (!newPage.url.trim()) {
      newErrors.url = 'Page URL is required';
    }
    
    // Basic URL validation
    if (newPage.url.trim() && !isValidUrl(newPage.url.trim())) {
      newErrors.url = 'Please enter a valid URL';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      // Create page using the API
      const pageData = {
        app_registry_id: application.id,
        page_title: newPage.page_title.trim(),
        url: formatUrl(newPage.url.trim()),
        restricted_access_options: newPage.restricted_access_options || {}
      };

      const createdPage = await apiClient.post('/pages', pageData);
      
      // Add the created page to the list
      setPages([...pages, createdPage]);
      setNewPage({ page_title: '', url: '', restricted_access_options: {} });
      setErrors({});
      
      showNotification('Page added successfully', 'success');
    } catch (err) {
      console.error('Error creating page:', err);
      setErrors({ submit: 'Failed to add page. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemovePage = async (pageId) => {
    if (!confirm('Are you sure you want to remove this page?')) {
      return;
    }
    
    try {
      // Delete page using the API
      await apiClient.delete(`/pages/${pageId}`);
      
      // Remove from local state
      setPages(pages.filter(page => page.id !== pageId));
      showNotification('Page removed successfully', 'success');
    } catch (err) {
      console.error('Error deleting page:', err);
      alert('Failed to remove page. Please try again.');
    }
  };

  const handleUpdatePage = async (pageId) => {
    const page = pages.find(p => p.id === pageId);
    if (!page) return;
    
    try {
      // Update page using the API
      const updatedData = {
        page_title: page.page_title,
        url: formatUrl(page.url),
        restricted_access_options: page.restricted_access_options || {},
        app_registry_id: application.id
      };
      
      const updatedPage = await apiClient.put(`/pages/${pageId}`, updatedData);
      
      // Update local state
      setPages(pages.map(p => p.id === pageId ? updatedPage : p));
      setEditingPage(null);
      showNotification('Page updated successfully', 'success');
    } catch (err) {
      console.error('Error updating page:', err);
      alert('Failed to update page. Please try again.');
    }
  };

  const showNotification = (message, type = 'info') => {
    if (type === 'error') {
      alert('Error: ' + message);
    } else {
      alert(message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPage(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddPage();
    }
  };

  const isValidUrl = (string) => {
    try {
      const url = new URL(string);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      // Try adding https:// if no protocol
      if (!string.includes('://')) {
        try {
          new URL('https://' + string);
          return true;
        } catch (_) {
          return false;
        }
      }
      return false;
    }
  };

  const formatUrl = (url) => {
    if (!url.includes('://')) {
      return 'https://' + url;
    }
    return url;
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="modal-backdrop show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1040
        }}
        onClick={onClose}
      />
      
      <div 
        className="modal show d-block" 
        style={{ zIndex: 1050 }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="ti-files me-2"></i>
                Register Application Pages - {application?.name}
              </h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="Close"
                disabled={isSubmitting}
              />
            </div>
            
            <div className="modal-body">
              {loading ? (
                <div className="text-center p-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading pages...</span>
                  </div>
                </div>
              ) : (
                <>
                  {/* Add New Page Form */}
                  <div className="card mb-4">
                    <div className="card-body">
                      <h6 className="card-title mb-3">Add New Page</h6>
                      <div className="row g-3">
                        <div className="col-md-5">
                          <label className="form-label">Page Title</label>
                          <input
                            type="text"
                            className={`form-control ${errors.page_title ? 'is-invalid' : ''}`}
                            name="page_title"
                            value={newPage.page_title}
                            onChange={handleInputChange}
                            onKeyPress={handleKeyPress}
                            placeholder="e.g., Dashboard"
                            disabled={isSubmitting}
                          />
                          {errors.page_title && (
                            <div className="invalid-feedback">
                              {errors.page_title}
                            </div>
                          )}
                        </div>
                        <div className="col-md-5">
                          <label className="form-label">URL</label>
                          <input
                            type="text"
                            className={`form-control ${errors.url ? 'is-invalid' : ''}`}
                            name="url"
                            value={newPage.url}
                            onChange={handleInputChange}
                            onKeyPress={handleKeyPress}
                            placeholder="e.g., https://app.example.com/dashboard"
                            disabled={isSubmitting}
                          />
                          {errors.url && (
                            <div className="invalid-feedback">
                              {errors.url}
                            </div>
                          )}
                        </div>
                        <div className="col-md-2">
                          <label className="form-label">&nbsp;</label>
                          <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={handleAddPage}
                            disabled={isSubmitting}
                          >
                            <i className="ti-plus me-1"></i> Add
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pages List */}
                  <div className="card">
                    <div className="card-body">
                      <h6 className="card-title mb-3">
                        Registered Pages ({pages.length})
                      </h6>
                      
                      {pages.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          <i className="ti-files display-4 mb-3 d-block"></i>
                          <p>No pages registered yet. Add your first page above.</p>
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th style={{ width: '40px' }}>#</th>
                                <th>Page Title</th>
                                <th>URL</th>
                                <th style={{ width: '150px' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pages.map((page, index) => (
                                <tr key={page.id || index}>
                                  <td className="text-muted">{index + 1}</td>
                                  <td>
                                    {editingPage === page.id ? (
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={page.page_title}
                                        onChange={(e) => {
                                          setPages(pages.map(p => 
                                            p.id === page.id 
                                              ? { ...p, page_title: e.target.value }
                                              : p
                                          ));
                                        }}
                                      />
                                    ) : (
                                      <strong>{page.page_title}</strong>
                                    )}
                                  </td>
                                  <td>
                                    {editingPage === page.id ? (
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={page.url}
                                        onChange={(e) => {
                                          setPages(pages.map(p => 
                                            p.id === page.id 
                                              ? { ...p, url: e.target.value }
                                              : p
                                          ));
                                        }}
                                      />
                                    ) : (
                                      <a 
                                        href={formatUrl(page.url)} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-primary text-decoration-none"
                                      >
                                        <i className="ti-link me-1"></i>
                                        {page.url}
                                      </a>
                                    )}
                                  </td>
                                  <td>
                                    {editingPage === page.id ? (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-success me-1"
                                          onClick={() => handleUpdatePage(page.id)}
                                          title="Save changes"
                                        >
                                          <i className="ti-check"></i>
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-secondary"
                                          onClick={() => {
                                            setEditingPage(null);
                                            loadApplicationPages();
                                          }}
                                          title="Cancel"
                                        >
                                          <i className="ti-close"></i>
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary me-1"
                                          onClick={() => setEditingPage(page.id)}
                                          disabled={isSubmitting}
                                          title="Edit page"
                                        >
                                          <i className="ti-pencil"></i>
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-danger"
                                          onClick={() => handleRemovePage(page.id)}
                                          disabled={isSubmitting}
                                          title="Remove page"
                                        >
                                          <i className="ti-trash"></i>
                                        </button>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>

                  {errors.submit && (
                    <div className="alert alert-danger mt-3">
                      <i className="ti-alert me-2"></i>
                      {errors.submit}
                    </div>
                  )}
                  
                  {pages.length > 0 && (
                    <div className="alert alert-info mt-3">
                      <i className="ti-info-alt me-2"></i>
                      <strong>Note:</strong> Pages are automatically saved. You can add access restrictions 
                      for each page in future updates.
                    </div>
                  )}
                </>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={onClose}
                disabled={isSubmitting}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ApplicationPagesModal;